import * as React from 'react'
import {
  StyleguideChapterOpts,
  StyleguideExampleOpts,
  StyleguideNavigationProps,
  StyleguidePageProps,
  StyleguidePartOpts,
  StyleguideSectionOpts,
  StyleguideTokenPartOpts,
  StyleguideTokenRowOpts,
} from '../types'

// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ----------     PAGE RENDERERS    ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------

export const StyleguidePage = (p: StyleguidePageProps) => {
  // useDocumentTitleEffect(`${p.config.title} | Styleguide`)
  return (
  <StyleguideChapter
    type={p.chapterType}
    slug={p.config.slug}
    title={p.config.title}
    intro={p.config.description}
    brand={p.config.brand}
  >
    {p.config.sections &&
      p.config.sections.length > 0 &&
      p.config.sections.map((section) => {
        return (
          <StyleguideSection
            key={`sg-section-${section.slug}`}
            type={p.chapterType}
            slug={section.slug}
            title={section.title}
            intro={section.description ? section.description : ''}
          >
            {section.renderer}
          </StyleguideSection>
        )
      })}
  </StyleguideChapter>
)}

export const StyleguideInfoPage = (p: StyleguidePageProps) => {
  // useDocumentTitleEffect(`${p.config.title} | Styleguide`)
  return (
  <StyleguideChapter type={p.chapterType} slug={p.config.slug} title={p.config.title} intro={p.config.description}>
    {p.config.renderer}
  </StyleguideChapter>
)}

// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ----------    BLOCK RENDERERS    ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------

export const StyleguideChapter = (props: StyleguideChapterOpts & { children: React.ReactNode }) => (
  <section className={`styleguide-chapter styleguide-chapter--${props.type.split('-')[0]} ${props.extraClasses || ''}`}>
    <span id={props.slug} className="styleguide__anchor" aria-hidden="true"></span>

    <header className="styleguide--styled styleguide-chapter__header">
      {props.title && (
        <h2
          className="styleguide__title styleguide-chapter__title"
          dangerouslySetInnerHTML={{ __html: `${props.title}${props.brand ? ` (${props.brand})` : ''}` }}
        />
      )}
      {props.intro && <p className="styleguide-chapter__intro">{props.intro}</p>}
    </header>

    <div className="styleguide-chapter__content">{props.children}</div>
  </section>
)

export const StyleguideSection = (props: StyleguideSectionOpts & { children: React.ReactNode }) => (
  <section className={`styleguide-section styleguide-section--${props.type} ${props.extraClasses || ''}`}>
    <span id={props.slug} className="styleguide__anchor" aria-hidden="true"></span>

    <header className="styleguide--styled styleguide-section__header">
      {props.title && <h3 className="styleguide-section__title">{props.title}</h3>}
      {props.intro && <p className="styleguide-section__intro">{props.intro}</p>}
    </header>

    <div className="styleguide-section__content">{props.children}</div>
  </section>
)

export const StyleguidePart = (props: StyleguidePartOpts & { children: React.ReactNode }) => (
  <section className={`styleguide-part ${props.extraClasses || ''}`}>
    {props.title && (
      <header className="styleguide--styled styleguide-part__header">
        <h4 className="styleguide-part__title">{props.title}</h4>
      </header>
    )}

    <div className="styleguide-part__content">
      {props.intro && (
        <div className="styleguide--styled styleguide-part__intro">
          <p className="styleguide-part__description">{props.intro}</p>
        </div>
      )}
      {props.children}
    </div>
  </section>
)

export const StyleguideTokenPart = (props: StyleguideTokenPartOpts & { children: React.ReactNode }) => (
  <table className={`styleguide-table ${props.extraClasses || ''}`}>
    <thead>
      <tr
        className={`styleguide-table__heading${
          props.hasExampleColumn == false || props.hasAttributeColumn == false
            ? ' styleguide-table__heading--2-column'
            : ''
        } styleguide--styled`}
      >
        <th className="styleguide-table__label styleguide-table__label--token">Token</th>
        {props.hasAttributeColumn == true && (
          <th className="styleguide-table__label styleguide-table__label--attributes">Attributes</th>
        )}
        {props.hasExampleColumn == true && (
          <th className="styleguide-table__label styleguide-table__label--example">Example</th>
        )}
      </tr>
    </thead>

    <tbody>{props.children}</tbody>
  </table>
)

export const StyleguideExample = (props: StyleguideExampleOpts & { children: React.ReactNode }) => (
  <section className="styleguide-example">
    {props.showExampleLabel == true && (
      <div className="styleguide--styled">
        <h5 className="styleguide-example__title">Example(s)</h5>
      </div>
    )}
    <div
      className={`styleguide-example__section cf ${props.extraClasses || ''}`}
      style={props.isFullScreen == true ? { padding: 0 } : undefined}
    >
      {props.isFullScreen == true ? (
        <div className="fs">
          <div className="fs__inner">{props.children}</div>
        </div>
      ) : (
        props.children
      )}
    </div>
  </section>
)

export const StyleguideTokenRow = (props: StyleguideTokenRowOpts & { children: React.ReactNode }) => (
  <>
    <tr className="styleguide-table__row">
      <td className="styleguide-table__cell styleguide--styled">
        <code className="styleguide-token__code">{props.code}</code>
        {props.description && <p className="styleguide-token__description">{props.description}</p>}
      </td>
      {props.attributes && (
        <td className="styleguide-table__cell styleguide--styled">
          {Array.isArray(props.attributes) ? (
            props.attributes.map((item, key) => {
              return (
                <span className="styleguide-token__attribute" key={key}>
                  {item}
                </span>
              )
            })
          ) : (
            <span className="styleguide-token__attribute">{props.attributes}</span>
          )}
        </td>
      )}
      {props.hasExample == true && (
        <td className={`styleguide-table__cell ${props.extraClasses || ''}`}>{props.children}</td>
      )}
    </tr>

    {props.hasSeparator == true && (
      <tr className="styleguide-table__row" aria-hidden="true">
        <td className="styleguide-table__seperator" colSpan={props.columnCount}></td>
      </tr>
    )}
  </>
)

// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ----------  NAVIGATION RENDERER  ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------

export const StyleguideNavigation = (p: StyleguideNavigationProps) => {
  const { breakpoint, config, currentPage, chapterType } = p
  const isActive = currentPage == chapterType
  return (
    <li
      className={`styleguide-nav__item styleguide-nav--${chapterType}${isActive ? ' styleguide-navitem--active' : ''}${
        isActive && config.sections && config.sections.length > 0 ? ' styleguide-navitem--toggled' : ''
      }`}
    >
      <div className="styleguide-nav__option">
        <a
          className="styleguide-nav__link"
          href={`/styleguide/${config.slug}`}
          dangerouslySetInnerHTML={{
            __html: `${config.title}${config.brand ? `<br/> <strong>${config.brand}</strong>` : ''}`,
          }}
        />
        {config.sections && config.sections.length > 0 && (
          <button
            className="styleguide-nav__toggler"
            onClick={(e) =>
              e.currentTarget.closest('.styleguide-nav__item')?.classList.toggle('styleguide-navitem--toggled')
            }
          >
            <span className="sr-text">Toggle</span>
          </button>
        )}
      </div>

      {config.sections && config.sections.length > 0 && (
        <ul className="styleguide-nav__sublist">
          {config.sections.map((section, key) => {
            return (
              <li className="styleguide-nav__item" key={`s-${chapterType}-${key}`}>
                <a
                  className="styleguide-nav__link"
                  href={`/styleguide/${config.slug}#${section.slug}`}
                  onClick={(e) => [
                    window.innerWidth < breakpoint &&
                      document.documentElement.classList.remove('styleguide-nav--toggled'),
                  ]}
                >
                  {section.title}
                </a>
              </li>
            )
          })}
        </ul>
      )}
    </li>
  )
}
